import type { UerpFeatureIcon } from "./uerpModuleIcons";

export interface ModuleFeature {
  title: string;
  icon: UerpFeatureIcon;
  description?: string;
  items?: string[];
}

export interface UerpModule {
  id: string;
  title: string;
  description: string;
  features: ModuleFeature[];
}

export const uerpModules: UerpModule[] = [
  {
    id: "student",
    title: "Student Portal",
    description:
      "Self-service portal for university and college students — admissions, academics, fees, exams, and campus life in one place.",
    features: [
      {
        title: "Online Admission",
        icon: "admission",
        description:
          "Apply for courses and programmes online, upload documents, track application status, and receive admission confirmations digitally.",
      },
      {
        title: "Course Registration",
        icon: "course-registration",
        description:
          "Register for semester courses, electives, and lab batches with credit limits, prerequisites, and seat availability checks.",
      },
      {
        title: "Academic Profile",
        icon: "profile",
        description:
          "View and update personal, academic, and contact details including programme, department, roll number, and mentor assignment.",
      },
      {
        title: "Timetable & Calendar",
        icon: "timetable",
        description:
          "Access lecture, lab, and tutorial schedules with academic calendar, holidays, and event notifications.",
      },
      {
        title: "Attendance Tracking",
        icon: "attendance",
        description:
          "Monitor subject-wise attendance percentage with alerts when below the minimum required threshold.",
      },
      {
        title: "Assignments & LMS",
        icon: "assignments",
        description:
          "Submit assignments, access study materials, and participate in online learning activities through integrated LMS.",
      },
      {
        title: "Examination Schedule",
        icon: "exam-schedule",
        description:
          "View internal, semester, and supplementary exam timetables with hall ticket download and seating arrangements.",
      },
      {
        title: "Results & CGPA",
        icon: "results",
        description:
          "Check semester results, grade cards, SGPA, CGPA, and revaluation status with downloadable transcripts.",
      },
      {
        title: "Fee Payment",
        icon: "fee-payment",
        description:
          "Pay tuition, examination, hostel, and other fees online with receipt generation and payment history.",
      },
      {
        title: "Scholarship Application",
        icon: "scholarship",
        description:
          "Apply for institutional and government scholarships with document upload and approval status tracking.",
      },
      {
        title: "Library Access",
        icon: "library",
        description:
          "Search catalogue, reserve books, view issue/return history, and pay fines through the digital library module.",
      },
      {
        title: "Hostel Management",
        icon: "hostel",
        description:
          "Apply for hostel accommodation, view room allotment, mess menu, and lodge maintenance requests.",
      },
      {
        title: "Placement Cell",
        icon: "placement",
        description:
          "Register for campus placements, view company drives, upload resumes, and track interview schedules.",
      },
      {
        title: "Notices & Communication",
        icon: "notices",
        description:
          "Receive department notices, circulars, and announcements via email, SMS, and in-app notifications.",
      },
    ],
  },
  {
    id: "faculty",
    title: "Faculty Portal",
    description:
      "Tools for professors, lecturers, and teaching staff to manage academics, assessments, and student engagement.",
    features: [
      {
        title: "Class & Batch Management",
        icon: "class-management",
        description:
          "Manage assigned courses, batches, and student lists across semesters and academic years.",
      },
      {
        title: "Attendance Marking",
        icon: "attendance-marking",
        description:
          "Mark daily lecture and lab attendance digitally with bulk upload and correction workflows.",
      },
      {
        title: "Lesson Planning",
        icon: "lesson-plan",
        description:
          "Upload syllabus coverage, lesson plans, and teaching schedules aligned to academic calendars.",
      },
      {
        title: "Assignment Management",
        icon: "assignment-mgmt",
        description:
          "Create, distribute, and evaluate assignments with deadline tracking and grade submission.",
      },
      {
        title: "Internal Assessment",
        icon: "internal-assessment",
        description:
          "Record internal marks, practical scores, and continuous evaluation components per university norms.",
      },
      {
        title: "Examination Duties",
        icon: "exam-duty",
        description:
          "View invigilation duties, paper setting assignments, and evaluation responsibilities.",
      },
      {
        title: "Student Mentoring",
        icon: "mentoring",
        description:
          "Track mentee progress, counselling notes, and academic intervention recommendations.",
      },
      {
        title: "Research & Projects",
        icon: "research",
        description:
          "Manage research projects, thesis supervision, and publication records for postgraduate students.",
      },
      {
        title: "Leave Management",
        icon: "leave",
        description:
          "Apply for leave, view approval status, and access substitute lecture scheduling.",
      },
      {
        title: "Payroll View",
        icon: "payroll",
        description:
          "Access salary slips, tax deductions, and payroll summaries through the faculty self-service portal.",
      },
    ],
  },
  {
    id: "admin",
    title: "Admin Module",
    description:
      "Centralised control for institution administrators — admissions, academics, HR, finance, and compliance.",
    features: [
      {
        title: "Institution Setup",
        icon: "institution-setup",
        description:
          "Configure campuses, faculties, departments, programmes, courses, and academic year structures.",
      },
      {
        title: "Admission Management",
        icon: "admission-mgmt",
        description:
          "Manage enquiry, application, merit lists, counselling rounds, and seat allotment across programmes.",
      },
      {
        title: "Student Lifecycle",
        icon: "student-lifecycle",
        description:
          "Handle enrolment, promotion, transfer, dropout, and alumni conversion with complete audit trails.",
      },
      {
        title: "Staff & HR Management",
        icon: "hr-management",
        description:
          "Maintain faculty and non-teaching staff records, designations, contracts, and organisational hierarchy.",
      },
      {
        title: "Role-Based Access",
        icon: "role-access",
        description:
          "Define granular permissions for deans, HODs, registrars, accountants, and department coordinators.",
      },
      {
        title: "Academic Governance",
        icon: "governance",
        description:
          "Manage boards of studies, academic council meetings, approvals, and policy document repositories.",
      },
      {
        title: "Reports & Analytics",
        icon: "analytics",
        description:
          "Generate enrolment, attendance, results, fee collection, and accreditation-ready reports.",
      },
      {
        title: "Notification Engine",
        icon: "notifications",
        description:
          "Broadcast alerts to students, faculty, and parents via email, SMS, and push notifications.",
      },
      {
        title: "Document Management",
        icon: "documents",
        description:
          "Store and manage certificates, transcripts, offer letters, and compliance documents securely.",
      },
      {
        title: "Audit & Compliance",
        icon: "compliance",
        description:
          "Maintain activity logs, NAAC/NBA documentation support, and regulatory compliance tracking.",
      },
    ],
  },
  {
    id: "examination",
    title: "Examination Module",
    description:
      "End-to-end examination management from paper setting to result publication and certificate issuance.",
    features: [
      {
        title: "Exam Planning",
        icon: "exam-planning",
        description:
          "Create exam sessions, define schemes, and schedule internal and university examinations.",
      },
      {
        title: "Hall Ticket Generation",
        icon: "hall-ticket",
        description:
          "Auto-generate hall tickets with student photos, subjects, centres, and seating plans.",
      },
      {
        title: "Question Paper Management",
        icon: "question-paper",
        description:
          "Secure question paper upload, encryption, and controlled distribution to exam centres.",
      },
      {
        title: "Online Examination",
        icon: "online-exam",
        description:
          "Conduct proctored and non-proctored online exams with timer, auto-submit, and anti-malpractice controls.",
      },
      {
        title: "Answer Sheet Evaluation",
        icon: "evaluation",
        description:
          "Digital mark entry, moderation, revaluation requests, and grace mark processing.",
      },
      {
        title: "Result Processing",
        icon: "result-processing",
        description:
          "Calculate SGPA, CGPA, ranks, and publish results with controlled release workflows.",
      },
      {
        title: "Transcript & Certificates",
        icon: "certificates",
        description:
          "Generate degree certificates, provisional certificates, and official transcripts on demand.",
      },
      {
        title: "Revaluation & Supplementary",
        icon: "revaluation",
        description:
          "Manage revaluation applications, fee collection, and supplementary exam registrations.",
      },
    ],
  },
  {
    id: "finance",
    title: "Finance Module",
    description:
      "Complete fee management, accounting integration, and financial reporting for educational institutions.",
    features: [
      {
        title: "Fee Structure Setup",
        icon: "fee-structure",
        description:
          "Define programme-wise, category-wise, and year-wise fee heads with instalment schedules.",
      },
      {
        title: "Online Fee Collection",
        icon: "online-fees",
        description:
          "Accept payments via UPI, cards, net banking, and payment gateways with auto-reconciliation.",
      },
      {
        title: "Concession & Waiver",
        icon: "concession",
        description:
          "Apply fee concessions, staff wards quotas, and category-based waivers with approval workflows.",
      },
      {
        title: "Scholarship Disbursement",
        icon: "scholarship-disburse",
        description:
          "Track scholarship credits, sponsor payments, and beneficiary reconciliation.",
      },
      {
        title: "Receipts & Invoices",
        icon: "receipts",
        description:
          "Generate GST-compliant receipts, fee demand notices, and downloadable payment invoices.",
      },
      {
        title: "Due & Defaulter Tracking",
        icon: "defaulter",
        description:
          "Monitor pending dues, send automated reminders, and restrict services for fee defaulters.",
      },
      {
        title: "Expense Management",
        icon: "expense",
        description:
          "Record departmental expenses, purchase orders, and budget utilisation against allocations.",
      },
      {
        title: "Financial Reports",
        icon: "financial-reports",
        description:
          "Daily collection summaries, ledger exports, and audit-ready financial statements.",
      },
    ],
  },
  {
    id: "library-hostel",
    title: "Library & Hostel",
    description:
      "Campus facility management for libraries, hostels, transport, and student welfare services.",
    features: [
      {
        title: "Library Catalogue",
        icon: "catalogue",
        description:
          "Digital catalogue with ISBN search, availability status, and reservation queues.",
      },
      {
        title: "Issue & Return",
        icon: "issue-return",
        description:
          "Barcode/RFID-based book issue, return, renewal, and overdue fine calculation.",
      },
      {
        title: "E-Library Resources",
        icon: "e-library",
        description:
          "Access e-journals, digital repositories, and subscribed online learning resources.",
      },
      {
        title: "Hostel Allotment",
        icon: "hostel-allotment",
        description:
          "Room allocation by preference, merit, and category with waiting list management.",
      },
      {
        title: "Mess & Catering",
        icon: "mess",
        description:
          "Mess menu planning, meal subscriptions, and feedback collection from residents.",
      },
      {
        title: "Maintenance Requests",
        icon: "maintenance",
        description:
          "Log hostel maintenance tickets for plumbing, electrical, and housekeeping issues.",
      },
      {
        title: "Transport Management",
        icon: "transport",
        description:
          "Bus route management, pass issuance, and GPS-based transport tracking for students.",
      },
      {
        title: "Visitor Management",
        icon: "visitor",
        description:
          "Register hostel visitors, gate pass generation, and security compliance logging.",
      },
    ],
  },
];

export const uerpAdvantages = [
  "Complete ERP for universities, institutions, and colleges — admissions to alumni in one platform",
  "Student, faculty, admin, examination, finance, and campus facility modules integrated seamlessly",
  "Semester-based academics with course registration, credits, SGPA, and CGPA management",
  "Online fee collection with scholarships, concessions, and automated defaulter tracking",
  "Digital examination workflow — hall tickets, online exams, evaluation, and result publication",
  "Library, hostel, transport, and placement cell modules for full campus digitisation",
  "Role-based access for registrars, deans, HODs, faculty, and department coordinators",
  "Email, SMS, and in-app notifications for notices, fees, exams, and academic updates",
  "Accreditation-ready reports and audit logs for NAAC, NBA, and regulatory compliance",
  "Cloud-hosted or on-premise deployment with mobile-responsive web and app access",
];

export const uerpWorkflowSteps = [
  {
    title: "Admission & Enrolment",
    text: "Students apply online, documents are verified, merit lists are published, and enrolled students receive portal credentials.",
  },
  {
    title: "Academic Setup",
    text: "Departments configure programmes, courses, timetables, and faculty assignments for the active semester.",
  },
  {
    title: "Daily Operations",
    text: "Faculty mark attendance, upload materials, and students register courses, pay fees, and access campus services.",
  },
  {
    title: "Assessment Cycle",
    text: "Internal assessments, assignments, and semester examinations are scheduled, conducted, and evaluated digitally.",
  },
  {
    title: "Results & Progression",
    text: "Results are processed, grade cards published, and students promoted or flagged for academic intervention.",
  },
  {
    title: "Graduation & Alumni",
    text: "Certificates and transcripts are issued, placement records archived, and graduates moved to the alumni portal.",
  },
];

export const uerpPlatformApps = [
  {
    title: "Student Portal",
    description:
      "Admissions, course registration, attendance, exams, fees, library, hostel, and placement — full self-service for students.",
    icon: "student",
  },
  {
    title: "Faculty Portal",
    description:
      "Class management, attendance, assessments, mentoring, research tracking, and payroll access for teaching staff.",
    icon: "faculty",
  },
  {
    title: "Admin Dashboard",
    description:
      "Institution setup, admissions, HR, academics governance, reports, compliance, and notification management.",
    icon: "admin",
  },
  {
    title: "Parent & Alumni",
    description:
      "Parents monitor ward progress, fee status, and attendance. Alumni access transcripts, events, and networking.",
    icon: "alumni",
  },
];
